import axios, { AxiosError } from "axios";
import {
  ActionEnum,
  AxisType,
  CountOrderByEnum,
  CountParametersTier1Tier2,
  Direction,
  ExternalEventType,
  GroupByEnum,
  JustificationStatus,
  MarkedStatus,
  RiskScoreType,
  SearchTargetType,
  SortingKeys,
  targetAxis,
  Tier3AxisOperator,
  TimeAxisRangeInput,
  TimeAxisType,
} from "./types";
import { ApiResult } from "@/common/api/ApiResult.model";
import { SharedState } from "@/common/state/index";
import { getErrStatusCode } from "@/common/api/err.handler";
import { GqlCallsKey, gqlCallResolver } from "@/mock/demo/gql.resolver";
import { delayBy, emptyFn } from "@/fp";
import { InformationType, Insight } from "@/common/models";

export type CountResult = { [key: string]: { count: number; trend: number } };
export type UniqusResult = { [key: string]: { uniqs: number } };

export const CountResultMapper = {
  idAndCount: (result: CountResult) => {
    return Object.entries(result).map(([id, val]) => {
      const f = SharedState.infoTypeByShortName.value[id];
      return {
        id,
        labelId: f ? f.id : "",
        count: val.count,
        trend: val.trend,
      };
    });
  },
  idAndUniqs: (result: UniqusResult) => {
    return Object.entries(result).map(([id, val]) => {
      const f = SharedState.infoTypeByShortName.value[id];
      return {
        id,
        labelId: f ? f.id : "",
        count: val.uniqs,
      };
    });
  },

  flat: (result: CountResult) => {
    return Object.entries(result).reduce((acc, [id, val]) => {
      acc[id] = val.count;
      return acc;
    }, {} as { [key: string]: number });
  },
};

export const formatProps = (obj: any): string => {
  let v = "";
  for (const key in obj) {
    if (key === "props") {
      // @ts-ignore
      v += obj.props.map((p) => `${p}\n`);
    } else {
      // @ts-ignore
      v += `${key}{\n` + formatProps(obj[key]);
      v += `\n}`;
    }
  }
  return v.replace(/,/g, "");
};

export const DO_NOT_QUOTE_ME: string[] = [
  Tier3AxisOperator.And,
  Tier3AxisOperator.Or,
  RiskScoreType.Insight,
  RiskScoreType.Total,
  RiskScoreType.TotalCritical,
  RiskScoreType.OverExposure,
  RiskScoreType.OverExposureCritical,
  RiskScoreType.OverSharing,
  RiskScoreType.OverSharingCritical,
  RiskScoreType.UnsecureSharing,
  RiskScoreType.UnsecureSharingCritical,
  RiskScoreType.LackOfLabeling,
  RiskScoreType.LackOfLabelingCritical,
  RiskScoreType.Total_information_sharing,
  RiskScoreType.HR_information_sharing,
  RiskScoreType.Legal_information_sharing,
  RiskScoreType.Financial_information_sharing,
  RiskScoreType.Governance_information_sharing,
  RiskScoreType.Business_information_sharing,
  RiskScoreType.Other_information_sharing,
  RiskScoreType.Total_Access,
  RiskScoreType.HR_Access,
  RiskScoreType.Legal_Access,
  RiskScoreType.Financial_Access,
  RiskScoreType.Governance_Access,
  RiskScoreType.Business_Access,
  RiskScoreType.Other_Access,
  RiskScoreType.Total_Information_Governance,
  RiskScoreType.HR_Information_Governance,
  RiskScoreType.Legal_Information_Governance,
  RiskScoreType.Financial_Information_Governance,
  RiskScoreType.Governance_Information_Governance,
  RiskScoreType.Business_Information_Governance,
  RiskScoreType.Other_Information_Governance,
  RiskScoreType.Total_exposure,
  RiskScoreType.HR_exposure,
  RiskScoreType.Legal_exposure,
  RiskScoreType.Financial_exposure,
  RiskScoreType.Governance_exposure,
  RiskScoreType.Business_exposure,
  RiskScoreType.Other_exposure,
  RiskScoreType.Total_stolen,
  RiskScoreType.HR_stolen,
  RiskScoreType.Legal_stolen,
  RiskScoreType.Financial_stolen,
  RiskScoreType.Governance_stolen,
  RiskScoreType.Business_stolen,
  RiskScoreType.Other_stolen,
  RiskScoreType.Total_loss,
  RiskScoreType.HR_loss,
  RiskScoreType.Legal_loss,
  RiskScoreType.Financial_loss,
  RiskScoreType.Governance_loss,
  RiskScoreType.Business_loss,
  RiskScoreType.Other_loss,
  RiskScoreType.Total_breach,
  RiskScoreType.HR_breach,
  RiskScoreType.Legal_breach,
  RiskScoreType.Financial_breach,
  RiskScoreType.Governance_breach,
  RiskScoreType.Business_breach,
  RiskScoreType.Other_breach,
  Direction.Ascend,
  Direction.Descend,
  ActionEnum.List,
  ActionEnum.Avg,
  ActionEnum.Max,
  ActionEnum.Min,
  ActionEnum.RecordCount,
  ActionEnum.Sum,
  ActionEnum.UniqCount,
  // Added with the June-2026 schema update. Any AxisType absent from this
  // list is serialised as a quoted string, which GraphQL rejects as an enum
  // value — the symptom is a 400 with x:{type:"AiContentRelation"} in the body.
  AxisType.AiContentRelation,
  AxisType.EventType,
  AxisType.ObjectSeverity,
  AxisType.ObjectContainer,
  AxisType.ObjectLabel,
  AxisType.Application,
  AxisType.Employee,
  AxisType.EmployeeReceiver,
  AxisType.EmployeeSender,
  AxisType.EntityLabel,
  AxisType.EventLabel,
  AxisType.NonPrivatePersonSender,
  AxisType.NonPrivatePersonReceiver,
  AxisType.ExternalReceiver,
  AxisType.ExternalSender,
  AxisType.Folder,
  AxisType.ContentLabel,
  AxisType.Group,
  AxisType.Insight,
  AxisType.LabelSeverityHigh,
  AxisType.LabelSeverityLow,
  AxisType.LabelSeverityMedium,
  AxisType.NamedEntity,
  AxisType.None,
  AxisType.OrganizationReceiver,
  AxisType.OrganizationSender,
  AxisType.Person,
  AxisType.PersonReceiver,
  AxisType.PersonSender,
  AxisType.PersonalAddress,
  AxisType.ProcessInstance,
  AxisType.Severity,
  AxisType.Topic,
  AxisType.Sensitivity,
  AxisType.PersonSenderType,
  AxisType.PersonReceiverType,
  AxisType.EmployeeSenderType,
  AxisType.EmployeeReceiverType,
  AxisType.EmployeeType,
  AxisType.ExternalReceiverType,
  AxisType.OrganizationReceiverType,
  AxisType.FolderType,
  AxisType.PersonalAddressType,
  AxisType.NonPrivatePersonSenderType,
  AxisType.NonPrivatePersonReceiverType,
  AxisType.InfoTypeLabel,
  AxisType.All,
  AxisType.UntrustedEmployeeReceiver,
  AxisType.UntrustedEmployeeReceiverType,
  AxisType.UntrustedOrganization,
  AxisType.UntrustedOrganizationType,
  AxisType.UntrustedPersonalAddress,
  AxisType.UntrustedPersonalAddressType,
  SortingKeys.Date,
  CountOrderByEnum.Count,
  CountOrderByEnum.Files,
  CountOrderByEnum.StartDate,
  CountOrderByEnum.EndDate,
  GroupByEnum.None,
  GroupByEnum.XType,
  GroupByEnum.XValue,
  GroupByEnum.YType,
  GroupByEnum.YValue,
  TimeAxisType.Day,
  TimeAxisType.Month,
  TimeAxisType.Total,
  TimeAxisType.Week,
  targetAxis.X,
  targetAxis.Y,
  SearchTargetType.Employees,
  SearchTargetType.ExternalContacts,
  SearchTargetType.Files,
  SearchTargetType.Folders,
  SearchTargetType.OrganizationContacts,
  SearchTargetType.Organizations,
  SearchTargetType.PersonalAddresses,
  // Event filter enums
  JustificationStatus.None,
  JustificationStatus.SentOnly,
  JustificationStatus.Replied,
  MarkedStatus.Marked,
  MarkedStatus.NotMarked,
  ...Object.values(ExternalEventType),
];

export const formatInputWithoutBraces = (obj: any): string => {
  const res = formatInput(obj);
  return res.substr(1, res.length - 2);
};

export const formatInput = (obj: any): string => {
  const addVal =
    (prev = "", isLast = false) =>
    // @ts-ignore
    ([key, val]) => {
      let v = key ? `${key}:` : "";
      if (typeof val === "object") {
        // handle array
        if (Array.isArray(val)) {
          v += "[";
          for (let i = 0; i < val.length; i++) {
            const isLast = i === val.length - 1;
            v += addVal(v, isLast)(["", val[i]]);
          }
          v += "]" + (isLast ? "" : ",");
          return v;
        } else {
          // handle object
          v += "{";
          const s = Object.entries(val);
          for (let i = 0; i < s.length; i++) {
            const isLast = i === s.length - 1;
            v += addVal(v, isLast)(s[i]);
          }
          v += "}" + (isLast ? "" : ",");
          return v;
        }
      } else {
        // handle string
        if (typeof val === "string") {
          if (DO_NOT_QUOTE_ME.includes(val)) {
            v += val + (isLast ? "" : ", ");
          } else {
            v += `"${val}"${isLast ? "" : ","} `;
          }
        } else {
          // handle number , boolean
          v += val + (isLast ? "" : ", ");
        }
        return v;
      }
    };

  const res = Object.entries(obj).reduce((acc, [key, val], index, arr) => {
    const isLast = index === arr.length - 1;
    acc += addVal("", isLast)([key, val]);
    return acc;
  }, "");
  return "{" + res + "}";
};

export const tier12Params = (params: CountParametersTier1Tier2) =>
  formatInput(params);

export const gql = <I, T>(compose: (input: I) => string) => {
  return async (input: I): Promise<ApiResult<T>> => {
    const { env, loggedUser } = SharedState;
    try {
      const query = compose(input);
      const res = await axios({
        method: "POST",
        baseURL: env.value.gql,
        url: "",
        headers: {
          authorization: loggedUser.value.authorization,
        },
        data: {
          query,
        },
      });
      return {
        success: true,
        value: res.data.data,
      };
    } catch (e) {
      return {
        success: false,
        // @ts-ignore
        code: getErrStatusCode(e),
      };
    }
  };
};

export type gQueryMapper<F, R> = (
  filter: F,
  z: {
    total: TimeAxisRangeInput;
    frame: TimeAxisRangeInput;
    indexed_insights: Record<string, Insight>;
    allInfoTypes: Record<string, InformationType>;
    default_label: InformationType;
    isInfoTypeChildOfOne: (payload: {
      child: string;
      parents: string[];
    }) => void;
  }
) => { query: string; mapper: (result: any, state?: any) => R };

export const setKey = (key: string) => key.replace(/\s/g, "");

export const trimSpaces = (str: string) =>
  str.replace(/\s/g, "").replace(/\r?\n|\r/g, "");

export const GqlCache = (function createCache() {
  let _disable = false;
  const _cache = new Map();

  const keysOrder: string[] = [];
  const addItem = (tag: GqlCallsKey, query: string, response: any) => {
    if (_disable) {
      return;
    }
    const key = createKey(tag, query);

    _cache.set(key, {
      time: Date.now(),
      response,
    });
    keysOrder.unshift(key);
    if (keysOrder.length > 20) {
      _cache.delete(keysOrder.pop());
    }
  };

  const createKey = (tag: GqlCallsKey, query: string) => tag + "___" + query;

  const hasItem = (tag: GqlCallsKey, query: string, cacheMaxTime: number) => {
    const key = createKey(tag, query);
    if (_cache.has(key)) {
      const item = _cache.get(key);
      const isStillValidTime = Date.now() - item.time < cacheMaxTime;
      if (!isStillValidTime) {
        _cache.delete(key);
      }
      return isStillValidTime;
    } else {
      return false;
    }
  };

  const clear = () => _cache.clear();

  const removeByTag = (tag: GqlCallsKey) => {
    const keysToRemove: string[] = [];
    Array.from(_cache.keys()).forEach((key) => {
      const itemTag = key.split("___")[0];
      if (itemTag === tag) {
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach((key) => {
      _cache.delete(key);
    });
  };

  const removeByKey = (tag: GqlCallsKey, query: string) => {
    _cache.delete(createKey(tag, query));
  };

  const getItem = (tag: GqlCallsKey, query: string) => {
    const key = createKey(tag, query);
    keysOrder.unshift(key);
    return _cache.get(key).response;
  };

  const disable = () => {
    clear();
    _disable = true;
  };

  const enable = () => {
    _disable = false;
  };

  const getQueryFromLastTag = (tag: GqlCallsKey) => {
    const cache_key = keysOrder.find((key) => key.startsWith(tag));
    if (cache_key) {
      return cache_key.split("___")[1];
    }
    return "";
  };

  const get_query_from_last_tag_out_of_many = (tags: GqlCallsKey[]) => {
    // existing keys
    const cache_keys = tags
      .map((tag) => keysOrder.findIndex((key) => key.startsWith(tag)))
      .filter((f) => f >= 0);
    if (cache_keys.length > 0) {
      // get the resent key and return the query
      const resent_key_index = Math.min(...cache_keys);
      const cache_key = keysOrder[resent_key_index];
      return cache_key.split("___")[1];
    } else {
      // no key
      return "";
    }
  };

  const describe = () => {
    console.log(_cache);
  };

  return {
    describe,
    addItem,
    hasItem,
    getItem,
    clear,
    disable,
    enable,
    removeByTag,
    removeByKey,
    getQueryFromLastTag,
    get_query_from_last_tag_out_of_many,
  };
})();

// @ts-ignore
window.gqlCache = GqlCache;

let DelayTime = 0;
let id = 0;
let called = 0;
const delayQuery = () => {
  called++;
  DelayTime = DelayTime + 1000;
  clearTimeout(id);
  id = setTimeout(() => {
    DelayTime = 0;
  }, 1500);
};

const on_going_queries: Record<string, true> = {};
// @ts-ignore
window.on_going_queries = on_going_queries;
const waitForQuery = (query: string): Promise<void> => {
  let id = 0;
  return new Promise((resolve) => {
    id = setInterval(() => {
      if (!on_going_queries[query]) {
        clearInterval(id);
        resolve();
      }
    }, 200);
  });
};

export interface GqlMockCall {
  delay?: number;
  payload: (input: any) => Promise<any>;
}

interface GqlMockPlan {
  delay: number;
  activate: boolean;
  calls: Record<Partial<GqlCallsKey>, GqlMockCall>;
}

const create_gql_client = () => {
  let _plan: GqlMockPlan;
  const create_mock_plan = (plan: GqlMockPlan) => {
    _plan = plan;
    // @ts-ignore
    window._gql_mock_plan = _plan;
  };

  //@ts-ignore
  window.inject_mock_gql_api = (
    calls: Record<Partial<GqlCallsKey>, GqlMockCall>
  ) => {
    if (_plan) {
      _plan.calls = {
        ..._plan.calls,
        ...calls,
      };
    }
  };

  const use_mock = (key: GqlCallsKey) =>
    _plan && _plan.activate && _plan.calls[key];

  const gql2 = <I, T>(
    callKey: GqlCallsKey,
    gQuery: gQueryMapper<I, T>,
    options?: {
      localhost_server?: boolean;
      cacheMaxTime?: number;
      delayTime?: number;
      req_state?: any;
    }
  ) => {
    return async function gqlCall(input: I): Promise<ApiResult<T>> {
      if (use_mock(callKey)) {
        console.log("MOCK:: fire gql query with tag:", callKey);
        await delayBy(_plan.calls[callKey].delay || _plan.delay);
        const value = await _plan.calls[callKey].payload(input);
        return {
          success: true,
          value,
        };
      }
      const {
        cacheMaxTime = 1000 * 60 * 10,
        delayTime = 0,
        req_state,
        localhost_server = false,
      } = options || {};

      if (delayTime) {
        await delayBy(delayTime);
      }

      let theCall = emptyFn();
      const {
        env,
        loggedUser,
        calculatedTimeFrame,
        allInfoTypes,
        isInfoTypeChildOfOne,
        default_label,
        indexed_insights,
      } = SharedState;
      const { frame, total } = calculatedTimeFrame.value;
      const { mapper, query } = gQuery(input, {
        frame,
        total,
        allInfoTypes: allInfoTypes.value,
        indexed_insights: indexed_insights.value,
        isInfoTypeChildOfOne,
        default_label: default_label.value as InformationType,
      });
      try {
        // LOGGER
        console.log(callKey, ",  query: ", query);
        if (on_going_queries[query]) {
          await waitForQuery(query);
        } else {
          on_going_queries[query] = true;
        }
        if (env.value.env === "demo") {
          const res = await gqlCallResolver<I, T>({
            type: callKey,
            payload: input,
          });
          const result = {
            success: true,
            value: mapper(res.data.data),
          };
          // @ts-ignore
          return result;
        } else {
          let res;
          if (GqlCache.hasItem(callKey, query, cacheMaxTime)) {
            res = GqlCache.getItem(callKey, query);
            delete on_going_queries[query];
          } else {
            const headers: any = {
              authorization: loggedUser.value.authorization,
            };

            let baseURL = env.value.gql;

            const is_localhost_server =
              localhost_server || env.value.env === "debug";

            if (is_localhost_server) {
              baseURL = "http://localhost:5004/graphql";
              //  baseURL = "http://localhost:3001/graphql";
              headers["x-user"] = "erez-debug";
              headers["x-organization"] =
                "a8a30680-5b7e-4b7c-a812-2591e2f90bbc";
              //  "6b7f7aa4-584c-4171-b6d4-ba52e9fa2870";
            }

            const shouldSuppressFilteredEventsError =
              (callKey === "exposure-events" || callKey === "tier3-events") &&
              query.includes("externalEventTypes");
            const MAX_RETRY_COUNT = shouldSuppressFilteredEventsError ? 0 : 4;

            theCall = async (retryCount = 0) => {
              let response;
              try {
                response = await axios({
                  method: "POST",
                  baseURL,
                  url: "",
                  headers: {
                    ...headers,
                    shouldRetry:
                      retryCount < MAX_RETRY_COUNT ? "true" : "false",
                  },
                  data: {
                    query,
                  },
                });
                return response;
              } catch (e) {
                if (retryCount < MAX_RETRY_COUNT) {
                  retryCount++;
                  await delayBy(retryCount * 1000);
                  // @ts-ignore
                  response = await theCall(retryCount);
                  return response;
                } else {
                  throw e;
                }
              }
            };
            res = await theCall();
            GqlCache.addItem(callKey, query, res);
            delete on_going_queries[query];
          }
          return {
            success: true,
            value: mapper(res.data.data, req_state),
          };
        }
      } catch (e) {
        delete on_going_queries[query];
        return {
          success: false,
          // @ts-ignore
          code: getErrStatusCode(e),
        };
      }
    };
  };

  return {
    gql2,
    create_mock_plan,
  };
};

export const { gql2, create_mock_plan } = create_gql_client();

export const replaceEvery =
  (replace: any) =>
  (val: any) =>
  <T>(result: T): T => {
    for (const key in result) {
      if (typeof result[key] === "object" && result[key] != replace) {
        replaceEvery(replace)(val)(result[key]);
      }
      if (result[key] == replace) {
        result[key] = val;
      }
    }
    return result;
  };

export const getCount = (arr?: Array<{ count: number }>) =>
  arr && arr.length > 0
    ? /* arr[0].count */ arr
        .filter((f) => f.count)
        .reduce((acc, item) => {
          acc += item.count;
          return acc;
        }, 0)
    : 0;

export const cord = (type: AxisType, values: string[] = []) => {
  if (values.length == 0) {
    return {
      type,
    };
  } else {
    return {
      type,
      value: values[0].length === 0 ? "none" : values[0].toString(),
    };
  }
};

export const cord3 = (type: AxisType, values: string[] = []) => {
  if (values.length == 0) {
    return {
      type,
    };
  } else {
    return {
      type,
      values,
    };
  }
};

export const getTier2Deltas = gql2<
  { xType: AxisType; ids: string[]; yType: AxisType; yItemId: string },
  { [key: string]: { files: number; count: number } }
>("contacts.get", ({ xType, ids, yItemId, yType }, timeFrame) => {
  const q: CountParametersTier1Tier2 = {
    coordinate: {
      x: cord(xType, ids),
      y: cord(yType, [yItemId]),
      z: timeFrame.frame,
    },
    action: ActionEnum.List,
  };
  return {
    query: `
    {
      tier2count(params:${formatInput(q)}){
        x{value}
        count
        files
      }
    }
    }`,
    mapper: (res) => {
      const { tier2count } = res;
      // @ts-ignore
      return tier2count.reduce((acc, item) => {
        const { x, count, files } = item;
        acc[x.value] = {
          count,
          files,
        };
        return acc;
      }, {} as { [key: string]: { count: number; files: number } });
    },
  };
});
